import { useState } from 'react';
import { Box, Button, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCheck, IconCopy, IconMail } from '@tabler/icons-react';
import { useCountdown } from 'src/common/useCountdown';
import { buildMailtoUrl, truncateForMailto } from 'src/common/utils/email/mailto';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { EmailDraft } from '../chat/types';

const MAILTO_MAX_BODY_LENGTH = 1800;
const MAILTO_HINT_DURATION_SECONDS = 20;

type Props = {
  draft: EmailDraft;
};

type FieldBlockProps = {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  emptyPlaceholder?: string;
  scrollable?: boolean;
};

function FieldBlock({
  label,
  value,
  copied,
  onCopy,
  emptyPlaceholder,
  scrollable,
}: FieldBlockProps) {
  const isEmpty = !value;
  return (
    <Paper bg="white" p="sm" radius="md" pos="relative">
      <Group justify="space-between" align="center" gap="xs">
        <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7">
          {label}
        </Text>
        {!isEmpty && (
          <Button
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            size="compact-xs"
            leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            onClick={onCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </Group>
      {isEmpty ? (
        <Text size="textSm" c="gray.6">
          {emptyPlaceholder}
        </Text>
      ) : (
        <Text
          size="textSm"
          c="gray.9"
          mah={scrollable ? 220 : undefined}
          style={{ whiteSpace: 'pre-wrap', overflowY: scrollable ? 'auto' : undefined }}
        >
          {value}
        </Text>
      )}
    </Paper>
  );
}

export function EmailDraftCard({ draft }: Props) {
  const clipboard = useClipboard({ timeout: 2000 });
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showHint, setShowHint] = useState(false);
  const hintCountdown = useCountdown(MAILTO_HINT_DURATION_SECONDS, showHint, () =>
    setShowHint(false),
  );

  const handleCopy = (field: string, value: string) => {
    clipboard.copy(value);
    setCopiedField(field);
  };

  const bodyTooLong = draft.body.length > MAILTO_MAX_BODY_LENGTH;

  const handleOpenInClient = () => {
    const url = buildMailtoUrl(draft.to, draft.subject, truncateForMailto(draft.body));
    window.open(url, '_self');
    setShowHint(true);
  };

  const fieldCopied = (field: string) => copiedField === field && clipboard.copied;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center">
          <IconMail size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Email draft
          </Text>
        </Group>

        <Stack gap="xs">
          <FieldBlock
            label="To"
            value={draft.to}
            copied={fieldCopied('to')}
            onCopy={() => handleCopy('to', draft.to)}
            emptyPlaceholder="No recipient — add one in your email client"
          />
          <FieldBlock
            label="Subject"
            value={draft.subject}
            copied={fieldCopied('subject')}
            onCopy={() => handleCopy('subject', draft.subject)}
          />
          <FieldBlock
            label="Body"
            value={draft.body}
            copied={fieldCopied('body')}
            onCopy={() => handleCopy('body', draft.body)}
            scrollable
          />
        </Stack>

        <Button
          variant="filled"
          color="orange"
          size="sm"
          leftSection={<IconMail size={14} />}
          onClick={handleOpenInClient}
          disabled={bodyTooLong}
          style={{ alignSelf: 'flex-start' }}
        >
          Open in email client
        </Button>

        {bodyTooLong && (
          <Text size="textXs" c="gray.7">
            This draft is too long to open in your email client — use the copy buttons to move it
            over field by field.
          </Text>
        )}

        {showHint && hintCountdown > 0 && (
          <Text size="textXs" c="gray.7">
            Your email app should now be opening with this draft. If nothing appears, use the copy
            buttons to paste the content into your email instead.
          </Text>
        )}
      </Flex>
    </Box>
  );
}
